import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import PhoneInput from "react-phone-input-2";
import { InputLabel, FormHelperText } from "@mui/material";

import messages from "../../assets/locale/messages";
import { getPhoneInputLocalization } from "../../utils/phoneInputLocalization";
import { getCountriesRequest } from "../../store/Lookups/actions";
import "./PhoneInputField.scss";

const PhoneInputField = ({
  id,
  name,
  country,
  value,
  onChange,
  onBlur,
  label,
  required,
  labelAdornment,
  inputWrapperClass,
  labelClassName,
  isInputHasErr,
  helperTextClass,
  helperText,
  errMsg,
  countryCodeEditable,
  disableDropdown,
  disabled,
  countryCode,
  placeholder,
  formValueIsFullInternational = false,
  authRoundedStyle = false,
  useLookupCountriesOnly = true,
  inputProps = {},
  preferredCountries = ["sa"],
  containerClass,
  style,
  onMount: onMountFromParent,
  ...props
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const countries = useSelector((state) => state.lookups.countries);
  const { inputsValidations, shared: sharedMessages } = messages[lang];
  const phoneLocalization = getPhoneInputLocalization(lang);
  const dispatch = useDispatch();

  const [dialCode, setDialCode] = useState("");
  const [countriesShortCodes, setCountriesShortCodes] = useState([]);
  const [selectedIso, setSelectedIso] = useState(() =>
    String(country || "sa").toLowerCase()
  );

  const wrapperStyle =
    authRoundedStyle && (selectedIso || country)
      ? {
          "--phone-auth-iso": `'${(
            selectedIso ||
            country ||
            "eg"
          ).toUpperCase()}'`,
          ...style,
        }
      : style;

  useEffect(() => {
    if (useLookupCountriesOnly) {
      dispatch(getCountriesRequest({ page: -1 }));
    }
  }, [dispatch, useLookupCountriesOnly]);

  useEffect(() => {
    if (countries.length) {
      setCountriesShortCodes(
        countries.map((c) => c.short_code.toLowerCase())
      );
    }
  }, [countries]);

  useEffect(() => {
    if (!formValueIsFullInternational) {
      setDialCode(countryCode || "966");
    }
  }, [countryCode, formValueIsFullInternational]);

  useEffect(() => {
    if (country) {
      setSelectedIso(String(country).toLowerCase());
    }
  }, [country]);

  const onlyCountries =
    useLookupCountriesOnly && countriesShortCodes.length > 0
      ? countriesShortCodes
      : undefined;

  const phoneInputValue = formValueIsFullInternational
    ? String(value ?? "").replace(/\D/g, "")
    : `${dialCode}${value ?? ""}`.replace(/\D/g, "");

  const valueForPhoneInput = phoneInputValue;

  const resolvedPlaceholder = (() => {
    if (placeholder != null && String(placeholder).trim() !== "") {
      return String(placeholder);
    }
    const fromInputProps = inputProps?.placeholder;
    if (
      fromInputProps != null &&
      String(fromInputProps).trim() !== ""
    ) {
      return String(fromInputProps);
    }
    return "";
  })();

  const wrapperClass = [
    "phone-input-wrapper",
    disabled ? "disabled" : "",
    inputWrapperClass || "",
    authRoundedStyle ? "phone-input-field--auth" : "",
    authRoundedStyle && isInputHasErr ? "phone-input-field--auth-error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const mergedContainerClass = [
    containerClass,
    authRoundedStyle ? "phone-input-auth-container" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const containerClassProp =
    mergedContainerClass.length > 0 ? mergedContainerClass : undefined;

  const handlePhoneChange = (fullValue, countryData) => {
    const iso = countryData?.countryCode;
    if (iso) {
      setSelectedIso(String(iso).toLowerCase());
    }
    if (!formValueIsFullInternational) {
      setDialCode(countryData?.dialCode || dialCode);
     
      const normalizedDial = String(countryData?.dialCode || "").replace(
        /\D/g,
        ""
      );
      const isoLower = iso ? String(iso).toLowerCase() : "";

      if (!normalizedDial && !isoLower) {
        onChange(fullValue, null);
        return;
      }

      let matchedCountry = null;

      if (isoLower) {
        matchedCountry = countries.find(
          (c) => String(c.short_code || "").toLowerCase() === isoLower
        );
      }
      
      if (!matchedCountry) {
        matchedCountry = countries.find(
          (c) =>
            String(c.dial_code || "").replace(/\D/g, "") === normalizedDial
        );
      }
        const dialLength = (countryData?.dialCode || "").replace(/\D/g, "").length;
        const valueWithoutDial = fullValue.replace(/\D/g, "").slice(dialLength);
        onChange(valueWithoutDial, matchedCountry);
    } else {
      onChange(fullValue, countryData);
    }
  };

  const phoneInput = (
    <PhoneInput
      key={`phone-input-${lang}`}
      {...props}
      id={id}
      name={name}
      country={country}
      className={isInputHasErr && !authRoundedStyle ? "error-border" : ""}
      value={valueForPhoneInput}
      onChange={handlePhoneChange}
      onMount={(v, data, formattedValue) => {
        onMountFromParent?.(v, data, formattedValue);
      }}
      onlyCountries={onlyCountries}
      preferredCountries={preferredCountries}
      containerClass={containerClassProp}
      countryCodeEditable={countryCodeEditable}
      disableCountryCode={false}
      disableDropdown={disableDropdown}
      disabled={disabled}
      placeholder={resolvedPlaceholder}
      localization={phoneLocalization}
      searchPlaceholder={sharedMessages.searchCountry}
      inputProps={{
        ...inputProps,
        name,
        id: id || name,
        ...(resolvedPlaceholder ? { placeholder: resolvedPlaceholder } : {}),
        ...(onBlur ? { onBlur } : {}),
      }}
    />
  );

  return (
    <div
      className={wrapperClass}
      dir={lang === "ar" ? "rtl" : "ltr"}
      style={wrapperStyle}>
      <InputLabel htmlFor={id || name} className={labelClassName}>
        <>
          {label}
          {required ? <span className="err-asterisk"> *</span> : ""}
          {labelAdornment && <span className="ms-2">{labelAdornment}</span>}
        </>
      </InputLabel>
      {authRoundedStyle ? (
        <div className="phone-input-field__auth-ltr" dir="ltr">
          {phoneInput}
        </div>
      ) : (
        phoneInput
      )}
      <FormHelperText
        error={isInputHasErr}
        className={`${helperTextClass || "font-medium fsize-12"} ${
          lang === "en" ? "" : "text-end"
        }`}>
        {isInputHasErr ? inputsValidations[errMsg] : helperText}
      </FormHelperText>
    </div>
  );
};

export default PhoneInputField;

PhoneInputField.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  country: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  required: PropTypes.bool,
  labelAdornment: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  labelClassName: PropTypes.string,
  inputWrapperClass: PropTypes.string,
  isInputHasErr: PropTypes.bool,
  countryCodeEditable: PropTypes.bool,
  disableDropdown: PropTypes.bool,
  errMsg: PropTypes.string,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  helperTextClass: PropTypes.string,
  phoneObj: PropTypes.object,
  disabled: PropTypes.bool,
  countryCode: PropTypes.string,
  placeholder: PropTypes.string,
  formValueIsFullInternational: PropTypes.bool,
  authRoundedStyle: PropTypes.bool,
  useLookupCountriesOnly: PropTypes.bool,
  inputProps: PropTypes.object,
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  containerClass: PropTypes.string,
  style: PropTypes.object,
  onMount: PropTypes.func,
};
